import { useEffect, useRef } from 'react';
import { buildWhatsAppUrl, WHATSAPP_MESSAGE_B2B2C } from '@/constants/whatsapp';
import { Button } from '@/components';
import { HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { E2E_LANDING } from '@/constants/e2eSchedulingTestIds';

interface HeroSectionProps {
	isPatientAudience: boolean;
	isIndividualAudience?: boolean;
	cheapestDisplayedPrice?: number | null;
}

const HeroSection = ({
	isPatientAudience,
	isIndividualAudience,
	cheapestDisplayedPrice,
}: HeroSectionProps) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const navigate = useNavigate();
	const formattedStartingPrice =
		typeof cheapestDisplayedPrice === 'number' &&
		Number.isFinite(cheapestDisplayedPrice)
			? cheapestDisplayedPrice.toLocaleString('pt-BR', {
					minimumFractionDigits: 2,
			  })
			: '59,97';

	const handleContact = () => {
		window.open(buildWhatsAppUrl(WHATSAPP_MESSAGE_B2B2C), '_blank');
	};

	const scrollToPlans = () => {
		const element = document.getElementById('planos');
		element?.scrollIntoView({ behavior: 'smooth' });
	};

	const scrollToHelp = () => {
		const element = document.getElementById('central-ajuda');
		element?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.defaultMuted = true;
			videoRef.current.muted = true;
			videoRef.current.play().catch((error) => {
				console.log('Autoplay bloqueado pelo navegador:', error);
			});
		}
	}, []);

	if (isPatientAudience) {
		return (
			<section
				className={`relative flex w-full items-center justify-center overflow-hidden bg-[#0c0c0c] px-6 md:px-10 ${
					isIndividualAudience
						? 'min-h-[80vh] py-16 md:py-20'
						: 'min-h-[90vh] py-20 md:py-24'
				}`}
			>
				<div className="pointer-events-none absolute inset-0">
					<div className="absolute left-1/2 top-[-220px] h-[680px] w-[940px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,113,70,0.23)_0%,transparent_62%)]" />
					<div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/40" />
				</div>
				<div
					className={`relative z-10 mx-auto flex w-full max-w-[760px] flex-col items-center px-4 text-center ${
						isIndividualAudience
							? 'space-y-4 md:space-y-5'
							: 'space-y-6 md:space-y-8'
					}`}
				>
					{isIndividualAudience ? (
						<>
							<div className="font-albert text-[11px] font-bold uppercase tracking-[0.22em] text-[#ffb187]">
								Terapia online · Plumaa
							</div>
							<h1 className="font-libre max-w-4xl text-[34px] font-bold leading-[1.08] tracking-[-0.015em] text-white md:text-[60px]">
								Terapia online{' '}
								<em className="font-libre text-brand-orange">
									todo mês.
								</em>
							</h1>
							<h2 className="font-libre text-[20px] font-normal text-white/45 md:-mt-3">
								A partir de{' '}
								<span className="font-bold text-brand-orange">
									R$ {formattedStartingPrice}
								</span>
							</h2>
							<p className="font-albert mx-auto max-w-[560px] text-[17px] font-light leading-[1.7] text-white/75 md:text-[18px]">
								Psicólogos certificados. Você escolhe o horário,
								faz a sessão pelo navegador, e cuida da sua
								cabeça sem peso no bolso.
							</p>
							<div className="flex flex-col items-center gap-5 pt-2">
								<Button
									onClick={scrollToPlans}
									className="
										font-albert rounded-lg bg-brand-orange
										px-10 py-4 text-[15px] font-bold normal-case
										tracking-[0.02em] text-white transition-all
										duration-200 hover:-translate-y-0.5
										hover:bg-brand-orange/90
										hover:shadow-[0_14px_36px_rgba(255,113,70,0.42)]
										active:scale-[0.99]
									"
									text="Escolher meu plano"
								/>
								<p className="font-albert text-[13px] text-white/55">
									Sem fidelidade · cancele quando quiser
								</p>
								<div className="mt-1 flex flex-wrap items-center justify-center gap-2">
									<span className="font-albert rounded-full border border-white/15 bg-white/[0.07] px-3.5 py-1.5 text-[12.5px] font-medium text-white/75">
										Psicólogos com CRP
									</span>
									<span className="font-albert rounded-full border border-white/15 bg-white/[0.07] px-3.5 py-1.5 text-[12.5px] font-medium text-white/75">
										100% online
									</span>
									<span className="font-albert rounded-full border border-white/15 bg-white/[0.07] px-3.5 py-1.5 text-[12.5px] font-medium text-white/75">
										Troca de profissional grátis
									</span>
								</div>
							</div>
						</>
					) : (
						<>
							<div className="inline-flex items-center rounded-full border border-brand-orange/40 bg-zinc-900/70 px-4 py-1.5 backdrop-blur-sm">
								<span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-orange">
									Cuidado individual e acessível
								</span>
							</div>

							<h1 className="max-w-4xl text-4xl font-black leading-[1.1] tracking-tighter text-white md:text-7xl md:leading-[1.05]">
								Sua saúde mental
								<br />
								<span className="text-brand-orange">
									no seu tempo.
								</span>
							</h1>

							<p className="mx-auto max-w-2xl text-base font-medium leading-relaxed text-zinc-200 md:text-xl">
								Conecte-se com os melhores psicólogos do Brasil
								em uma plataforma segura, humana e feita para o
								seu bem-estar.
							</p>

							<div className="flex flex-col items-center gap-4 pt-2 md:flex-row">
								<Button
									data-testid={
										E2E_LANDING.SCHEDULE_CONSULTATION
									}
									onClick={() => navigate('/consulta')}
									className="
										rounded-full border-2 border-white 
										bg-brand-orange px-12 py-4 text-xs 
										font-black uppercase tracking-[0.15em] 
										text-white shadow-brand-white 
										transition-all
										duration-150 hover:translate-x-[2px] 
										hover:translate-y-[2px] hover:bg-brand-orange/90
										hover:shadow-none
										active:scale-90
									"
									text="Agendar consulta"
								/>
								<Button
									onClick={scrollToPlans}
									className="
										rounded-full border-2 border-brand-orange 
										bg-white px-12 py-4 text-xs 
										font-black uppercase tracking-[0.15em] 
										text-brand-orange shadow-brand-orange 
										transition-all
										duration-150 hover:translate-x-[2px] 
										hover:translate-y-[2px] hover:bg-zinc-100
										hover:shadow-none
										active:scale-90
									"
									text="Ver planos"
								/>
							</div>
						</>
					)}
				</div>
			</section>
		);
	}

	return (
		<section className="relative flex h-[90vh] min-h-[650px] w-full flex-col justify-center overflow-hidden p-6">
			{/* FUNDO: VÍDEO + OVERLAY ESCURO */}
			<div className="absolute inset-0 z-0 h-full w-full">
				<video
					ref={videoRef}
					className="block h-full w-full object-cover object-center"
					autoPlay
					loop
					muted
					playsInline
					src="https://res.cloudinary.com/dd5pbp2tb/video/upload/v1765398095/v1_1_1_ktuobv.mp4"
				/>
				<div className="pointer-events-none absolute inset-0 bg-black/60"></div>
			</div>

			{/* CONTEÚDO CENTRALIZADO */}
			<div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-center space-y-6 px-4 text-center md:space-y-8 md:px-20">
				<div className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 backdrop-blur-sm">
					<span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-orange">
						Inteligência em Saúde Mental
					</span>
				</div>

				<h1 className="mx-auto max-w-4xl text-4xl font-black leading-[1.1] tracking-tighter text-white md:text-7xl md:leading-[1.05]">
					Faz total sentido pedir Plumaa ao seu{' '}
					<span className="text-brand-orange">RH.</span>
				</h1>

				<p className="mx-auto max-w-2xl text-base font-medium leading-relaxed text-zinc-200 md:text-xl">
					Atendimento psicológico disponível para quem trabalha com
					você.
				</p>

				{/* BOTÃO E DÚVIDAS CENTRALIZADOS */}
				<div className="flex flex-col items-center gap-6 pt-2 md:pt-4">
					<Button
						onClick={handleContact}
						className="
									rounded-full border-2 border-white 
									bg-brand-orange px-12 py-4 text-xs 
									font-black uppercase tracking-[0.15em] 
									text-white shadow-brand-white 
									transition-all
									duration-150 hover:translate-x-[2px] 
									hover:translate-y-[2px] hover:bg-brand-orange/90
									hover:shadow-none
									active:scale-90
								"
						text="Quero Plumaa"
					/>

					<div
						onClick={scrollToHelp}
						className="group flex cursor-pointer items-center gap-2 text-zinc-300 transition-colors hover:text-white"
					>
						<HelpCircle
							size={16}
							className="text-zinc-400 group-hover:text-white"
						/>
						<p className="text-[11px] font-medium">
							Alguma dúvida?{' '}
							<span className="underline underline-offset-4">
								Central de ajuda
							</span>
						</p>
					</div>
				</div>
			</div>
		</section>
	);
};

export { HeroSection };
